import { useEffect, useState } from 'react'
import {
  ArrowCounterClockwiseIcon,
  getSampleProgress,
  Mtds,
  Sample,
  WarningIcon,
  XIcon
} from 'prove-common'

type SampleProgressStep = ReturnType<typeof getSampleProgress>[number]

const getInitialDirection = (): 'down' | 'right' => {
  if (globalThis.window === undefined) {
    return 'right'
  }

  if (window.innerWidth < 1024) {
    return 'down'
  }

  return 'right'
}

const getStepColor = (
  step: SampleProgressStep,
  index: number,
  currentindex: number
) => {
  if (step.isWarning) {
    return 'warning'
  }

  if (step.isUndo) {
    return 'info'
  }

  if (step.isCompleted === false && index < currentindex) {
    return 'neutral'
  }

  return undefined
}

const getStepIcon = (
  step: SampleProgressStep,
  index: number,
  currentindex: number
) => {
  if (step.isWarning) {
    return <WarningIcon />
  }

  if (step.isUndo) {
    return <ArrowCounterClockwiseIcon />
  }

  if (step.isCompleted === false && index < currentindex) {
    return <XIcon />
  }

  return undefined
}

const getCompletedAtText = (
  step: SampleProgressStep,
  index: number,
  currentindex: number
) => {
  if (step.completedAt) {
    return step.completedAt
  }

  if (step.isCompleted) {
    return ''
  }

  if (index < currentindex) {
    return '-'
  }

  return ''
}

const getStepKeyBase = (step: SampleProgressStep): string =>
  [
    step.label,
    step.completedAt ?? '',
    step.isCompleted ? 'done' : 'pending',
    step.isWarning ? 'warning' : 'normal',
    step.isUndo ? 'undo' : 'forward'
  ].join('-')

export const SampleProgress = ({ sample }: { sample: Sample }) => {
  const steps = getSampleProgress(sample)
  const state = steps.at(-1)?.isCompleted ? 'complete' : undefined
  const currentindex = steps.findLastIndex((step) => step.isCompleted)

  const [direction, setDirection] = useState<'down' | 'right'>(
    getInitialDirection
  )

  useEffect(() => {
    const onResize = () =>
      setDirection(window.innerWidth < 1024 ? 'down' : 'right')
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return (
    <Mtds.Steps
      data-fade='true'
      data-state={state}
      data-direction={direction}
      className={direction === 'right' ? 'flex-wrap' : ''}
    >
      {steps.map((step, index) => (
        <li
          key={`${getStepKeyBase(step)}-${index}`}
          aria-current={index === currentindex ? 'step' : undefined}
          data-color={getStepColor(step, index, currentindex)}
        >
          <mark>{getStepIcon(step, index, currentindex)}</mark>

          <strong>{step.label}</strong>
          <br />
          <small>{getCompletedAtText(step, index, currentindex)}</small>
        </li>
      ))}
    </Mtds.Steps>
  )
}
